import React, { useEffect, useState } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowLeft, Calendar, FileText } from "lucide-react";
import MemberShell from "@/components/MemberShell";
import { authHeaders } from "@/lib/auth";
import { useT } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /member/digests — history of all sent monthly digests this member can read.
 * Clicking a row opens /member/digests/:month with the full body.
 */
export default function MemberDigests() {
    const { lang } = useT();
    const isCn = lang === "cn";
    const nav = useNavigate();
    const [digests, setDigests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [email, setEmail] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [me, list] = await Promise.all([
                    axios.get(`${API}/auth/me`, { headers: authHeaders() }),
                    axios.get(`${API}/member/hub/digests`, { headers: authHeaders() }),
                ]);
                if (cancelled) return;
                setEmail(me?.data?.member?.email || "");
                setDigests(Array.isArray(list?.data) ? list.data : []);
            } catch (err) {
                if (err?.response?.status === 401) {
                    nav("/member/login?next=/member/digests", { replace: true });
                    return;
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [nav]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-cafe-cream">
                <Loader2 className="h-8 w-8 animate-spin text-cafe-olive" />
            </div>
        );
    }

    return (
        <MemberShell email={email}>
            <Link to="/member" className="inline-flex items-center text-cafe-olive hover:text-cafe-oliveDark text-sm mb-6" data-testid="digests-back">
                <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> {isCn ? "返回 Hub" : "Back to Hub"}
            </Link>
            <h1 className="font-display text-3xl md:text-4xl text-cafe-espresso mb-2 leading-tight">
                {isCn ? "月度 Digest 存档" : "Monthly Digests"}
            </h1>
            <p className="text-cafe-mocha text-sm mb-8">
                {isCn ? "查看每月发送的趋势与系统更新。" : "Every monthly trend report I've sent — re-read anytime."}
            </p>

            {digests.length === 0 ? (
                <div className="text-cafe-mocha/70 py-16 text-center bg-white/60 border border-dashed border-cafe-espresso/15 rounded-2xl" data-testid="digests-empty">
                    <FileText className="h-8 w-8 mx-auto mb-4 text-cafe-mocha/40" />
                    <p className="text-sm italic">{isCn ? "暂无 digest。" : "No digests yet."}</p>
                </div>
            ) : (
                <div className="space-y-3" data-testid="digests-list">
                    {digests.map((d) => (
                        <Link
                            key={d.month}
                            to={`/member/digests/${d.month}`}
                            data-testid={`digest-row-${d.month}`}
                            className="block bg-cafe-cream border border-cafe-espresso/10 rounded-2xl p-5 hover:border-cafe-olive/40 hover:shadow-md transition-all"
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                    <div className="text-[10px] tracking-[0.22em] uppercase text-cafe-olive font-semibold flex items-center gap-1.5 mb-1.5">
                                        <Calendar className="h-3 w-3" /> {d.month}
                                    </div>
                                    <h3 className="font-display text-lg text-cafe-espresso leading-tight mb-1">{d.subject || "Untitled"}</h3>
                                    {d.intro && (
                                        <p className="text-sm text-cafe-mocha line-clamp-2 leading-relaxed">{d.intro}</p>
                                    )}
                                </div>
                                <span className="text-[11px] text-cafe-mocha/70 whitespace-nowrap">
                                    {d.sent_at ? new Date(d.sent_at).toLocaleDateString("en-MY", { month: "short", day: "numeric" }) : ""}
                                </span>
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </MemberShell>
    );
}


export function MemberDigestDetail() {
    const { month } = useParams();
    const { lang } = useT();
    const isCn = lang === "cn";
    const nav = useNavigate();
    const [digest, setDigest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [email, setEmail] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [me, d] = await Promise.all([
                    axios.get(`${API}/auth/me`, { headers: authHeaders() }),
                    axios.get(`${API}/member/hub/digest/${month}`, { headers: authHeaders() }),
                ]);
                if (cancelled) return;
                setEmail(me?.data?.member?.email || "");
                setDigest(d?.data || null);
            } catch (err) {
                if (err?.response?.status === 401) {
                    nav(`/member/login?next=/member/digests/${month}`, { replace: true });
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [month, nav]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-cafe-cream">
                <Loader2 className="h-8 w-8 animate-spin text-cafe-olive" />
            </div>
        );
    }
    if (!digest) {
        return <MemberShell email={email}><p>Not found.</p></MemberShell>;
    }

    return (
        <MemberShell email={email}>
            <Link to="/member/digests" className="inline-flex items-center text-cafe-olive hover:text-cafe-oliveDark text-sm mb-6">
                <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> {isCn ? "返回存档" : "Back to archive"}
            </Link>
            <div className="text-[10px] tracking-[0.22em] uppercase text-cafe-olive font-semibold mb-2">{digest.month}</div>
            <h1 className="font-display text-3xl md:text-4xl text-cafe-espresso mb-4 leading-tight">{digest.subject || "Untitled"}</h1>
            {digest.intro && <p className="text-cafe-mocha mb-8 leading-relaxed">{digest.intro}</p>}
            {digest.body_html && (
                <div
                    className="prose max-w-none text-cafe-espresso prose-headings:font-display prose-headings:text-cafe-espresso prose-a:text-cafe-olive prose-strong:text-cafe-espresso bg-cafe-cream border border-cafe-espresso/10 rounded-2xl p-6 md:p-8"
                    data-testid="digest-body"
                    dangerouslySetInnerHTML={{ __html: digest.body_html }}
                />
            )}
        </MemberShell>
    );
}
